import React from 'react';
import moment from 'moment';
import { GrantDTO, GranTypeDTO } from '../../../../Profile/Accounts/api/useAccountsQuery';
import { TableRow } from '../TableRow/TableRow';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { formatCurrency, formatNumber } from '../../../../CommonUtils/formatNumber';
import { DATE_FORMAT } from '../../../../CommonComponents/OPL/dates/utils';
import { OptionsPerGrantTypeDTO } from '../../api/useStockOptionsQuery';
import { ExerciseStrategyDTO } from '../../StockOptions.meta';
import { VestingProgressSummary } from '../VestingProgressSummary/VestingProgressSummary';
import { HtmlTable } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';

interface Props {
  grant: GrantDTO;
  options: OptionsPerGrantTypeDTO[];
}

export const VestingListTable = ({ grant, options }: Props) => {
  if ((grant.VestingList || []).length === 0) {
    return null;
  }

  const selectedGrant = options.find(item => item.GrantType === grant.GrantType);

  const isRsu = grant.GrantType === GranTypeDTO.Rsu;
  const isIsoNso = grant.GrantType === GranTypeDTO.Iso || grant.GrantType === GranTypeDTO.Nso;

  const totalShares = grant.TotalShares ?? 0;
  const totalVestedShares = grant.VestingList.reduce(
    (acc, vest) => acc + (vest.VestedShares ?? 0),
    0,
  );
  const ownedSharesCount = grant.OwnedShares ?? 0;
  const sharesToAllocate = totalShares - totalVestedShares - ownedSharesCount;

  // API dates are UTC and displayed as such, so reinterpret them as plain calendar days before
  // comparing to today. Mixing a UTC date with a local now shifts statuses by a day near midnight.
  const today = moment();
  const toCalendarDay = (value: string) =>
    moment(moment.utc(value).format(DATE_FORMAT), DATE_FORMAT);

  const alreadyVestedShares = grant.VestingList.reduce(
    (acc, vest) =>
      acc +
      (vest.VestingDate && toCalendarDay(vest.VestingDate).isSameOrBefore(today, 'day')
        ? vest.VestedShares ?? 0
        : 0),
    0,
  );
  const yetToVestShares = totalVestedShares - alreadyVestedShares;
  const rsuVestedAndOwned = alreadyVestedShares + ownedSharesCount;

  return (
    <Box py="12">
      <VestingProgressSummary
        isRsu={isRsu}
        totalShares={totalShares}
        alreadyVestedShares={alreadyVestedShares}
        yetToVestShares={yetToVestShares}
        ownedSharesCount={ownedSharesCount}
        rsuVestedAndOwned={rsuVestedAndOwned}
        sharesToAllocate={sharesToAllocate}
        px="16"
      />

      <Box px="16">
        <HtmlTable variant="embedded">
          <table>
            <thead>
              <tr>
                <th>
                  <Text variant="smallCaps" textAlign="left">
                    Vest Date
                  </Text>
                </th>
                <th>
                  <Text variant="smallCaps" textAlign="left">
                    Shares
                  </Text>
                </th>

                {isIsoNso && (
                  <>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Status
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Exercise Date
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Strike Price
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Expiration Date
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Shares to Exercise
                      </Text>
                    </th>
                  </>
                )}

                {isRsu && (
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Status
                    </Text>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {grant.VestingList.map(vest => (
                <TableRow key={vest.Id}>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      {moment.utc(vest.VestingDate).format(DATE_FORMAT)}
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={500} textAlign="left">
                      {vest.VestedShares ? formatNumber(vest.VestedShares) : 0}
                    </Text>
                  </td>

                  {isIsoNso && (
                    <>
                      <td>
                        <Text fontWeight={500} textAlign="left">
                          {!vest.VestingDate ||
                          toCalendarDay(vest.VestingDate).isAfter(today, 'day')
                            ? 'Scheduled to Vest'
                            : vest.ExerciseDate &&
                              toCalendarDay(vest.ExerciseDate).isSameOrBefore(today, 'day')
                            ? 'Exercised'
                            : 'Vested'}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} textAlign="left">
                          {vest.ExerciseStrategy === ExerciseStrategyDTO.AtPrincipalRetirement ||
                          vest.ExerciseStrategy === ExerciseStrategyDTO.AtSpouseRetirement
                            ? selectedGrant?.AvailableExerciseOptions?.find(
                                opt => opt.Value === vest.ExerciseStrategy,
                              )?.Label
                            : vest.ExerciseDate
                            ? moment.utc(vest.ExerciseDate).format(DATE_FORMAT)
                            : ''}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} textAlign="left">
                          {vest.PricePerShare
                            ? formatCurrency(vest.PricePerShare, { mantissa: 2 })
                            : ''}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} textAlign="left">
                          {vest.ExpirationDate
                            ? moment.utc(vest.ExpirationDate).format(DATE_FORMAT)
                            : ''}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" fontWeight={500} textAlign="left">
                          {formatNumber(vest.SharesToExercise)}
                        </Text>
                      </td>
                    </>
                  )}
                  {isRsu && (
                    <td>
                      <Text fontWeight={500} textAlign="left">
                        {vest.VestingDate &&
                        toCalendarDay(vest.VestingDate).isSameOrBefore(today, 'day')
                          ? 'Vested and Owned'
                          : 'Scheduled to Vest'}
                      </Text>
                    </td>
                  )}
                </TableRow>
              ))}
              {isIsoNso && ownedSharesCount > 0 && (
                <TableRow>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      {grant.OwnedExerciseDate
                        ? moment.utc(grant.OwnedExerciseDate).format(DATE_FORMAT)
                        : '—'}
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={500} textAlign="left">
                      {formatNumber(ownedSharesCount)}
                    </Text>
                  </td>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      Exercised and Owned
                    </Text>
                  </td>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      {grant.OwnedExerciseDate
                        ? moment.utc(grant.OwnedExerciseDate).format(DATE_FORMAT)
                        : ''}
                    </Text>
                  </td>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      {grant.OwnedStrikePrice
                        ? formatCurrency(grant.OwnedStrikePrice, { mantissa: 2 })
                        : ''}
                    </Text>
                  </td>
                  <td>
                    <Text fontWeight={500} textAlign="left">
                      —
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={500} textAlign="left">
                      0
                    </Text>
                  </td>
                </TableRow>
              )}
            </tbody>
          </table>
        </HtmlTable>
      </Box>
    </Box>
  );
};
